import React, { useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';

type ColumnConfig = {
  title: string;
  span?: number;
  align?: 'left' | 'center' | 'right';
  className?: string;
};

type FilterConfig = {
  span?: number;
  content: React.ReactNode;
  className?: string;
};

interface TableFilterLayoutProps {
  columns: ColumnConfig[];
  filters: FilterConfig[];
  className?: string;
  style?: React.CSSProperties;
  headerClassName?: string;
  filtersClassName?: string;
  innerClassName?: string;
  /** When true, the filter row is visible; when false, only column headers show */
  showFilters?: boolean;
  /** Called with the measured pixel height whenever the component resizes */
  onHeightChange?: (height: number) => void;
}

const getSpanClass = (span?: number) => {
  const safeSpan = Math.min(Math.max(span ?? 1, 1), 12);
  return `col-span-${safeSpan}`;
};

export function TableFilterLayout({
  columns,
  filters,
  className,
  style,
  headerClassName,
  filtersClassName,
  innerClassName,
  showFilters = true,
  onHeightChange,
}: TableFilterLayoutProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onHeightChange || !ref.current) return;
    const el = ref.current;
    const report = () => onHeightChange(el.getBoundingClientRect().height);
    report();
    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange, showFilters]);

  return (
    <div ref={ref} style={style} className={cn('bg-sidebar-hover border-b border-border', className)}>
      <div className={cn('px-6 py-3', showFilters ? 'space-y-3' : '', innerClassName)}>
        <div className={cn('grid grid-cols-12 gap-4 text-xs font-medium uppercase tracking-wider text-content-text-muted select-none cursor-default', headerClassName)}>
          {columns.map((column, index) => (
            <div
              key={`${column.title}-${index}`}
              className={cn(
                getSpanClass(column.span),
                column.align === 'center' && 'text-center',
                column.align === 'right' && 'text-right',
                column.className
              )}
            >
              {column.title}
            </div>
          ))}
        </div>

        {showFilters && (
          <div className={cn('grid grid-cols-12 gap-4', filtersClassName)}>
            {filters.map((filter, index) => (
              <div key={index} className={cn(getSpanClass(filter.span), filter.className)}>
                {filter.content}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



